<template>
  <div>
    <h1 class="heading-1 mb-24">{{ i18n(translations.title) }}</h1>
    <p class="body-1 mb-24">{{ i18n(translations.description) }}</p>

    <div class="!border-static-default-mid !rounded-sm border-1">
      <RevAccordionItem
        :collapsed="primaryAccordionState"
        @click="handlePrimaryAccordionClick"
      >
        <template #title>
          <span class="body-1-bold">
            {{ i18n(translations.primaryBeneficialOwner) }}
          </span>
        </template>
        <template #body>
          <RevCheckbox
            id="same_as_account_requester"
            v-model="isSameAsAccountRequester"
            class="mb-32"
            :disabled="isAccountRequesterCheckboxDisabled"
            @change="
              (value) => handleCheckSameAsAccountRequester(value.target.checked)
            "
          >
            <template #label>
              {{ i18n(translations.sameAsAccountRequesterLabel) }}
            </template>
          </RevCheckbox>
          <RevCheckbox
            id="same_as_legal_representative"
            v-model="isSameAsLegalRepresentative"
            class="mb-32"
            :disabled="isLegalRepresentativeCheckboxDisabled"
            @change="
              (value) =>
                handleCheckSameAsLegalRepresentative(value.target.checked)
            "
          >
            <template #label>
              {{ i18n(translations.sameAsLegalRepresentativeLabel) }}
            </template>
          </RevCheckbox>
          <StakeholderForm
            :description="i18n(translations.description)"
            :disabled="isFormDisabled"
            :feature-code="FeatureCode.WEB_KYB_BUSINESS_DETAILS"
            :form-id="FORM_ID"
            :stakeholder-data="beneficialOwner.primary"
            :title="i18n(translations.title)"
            @update:model-value="handleCheckSameAsLegalRepresentative"
          />
        </template>
      </RevAccordionItem>
    </div>

    <!-- Secondary Beneficial Owners -->
    <div v-if="secondaryBeneficialOwners.length > 0" class="mt-24">
      <div
        v-for="(owner, index) in secondaryBeneficialOwners"
        :key="owner.id"
        class="!border-static-default-mid !rounded-sm mb-16 border-1"
      >
        <RevAccordionItem
          :collapsed="owner.accordionState"
          @click="handleSecondaryAccordionClick(index)"
        >
          <template #title>
            <span class="body-1-bold">
              {{ i18n(translations.additionalBeneficialOwner) }} {{ index + 1 }}
            </span>
          </template>
          <template #body>
            <StakeholderForm
              :disabled="false"
              :feature-code="FeatureCode.WEB_KYB_BUSINESS_DETAILS"
              :form-id="`kyb_beneficial_owner_secondary_${owner.id}`"
              :stakeholder-data="owner.data"
            />
            <div class="mt-24 flex justify-center">
              <RevButton
                class="!border-static-danger-hi !text-static-danger-hi"
                variant="secondary"
                @click="removeSecondaryBeneficialOwner(index)"
              >
                {{ i18n(translations.removeBeneficialOwner) }}
              </RevButton>
            </div>
          </template>
        </RevAccordionItem>
      </div>
    </div>

    <div class="mt-24 flex justify-center">
      <RevButton
        class="w-full"
        variant="secondary"
        @click="addSecondaryBeneficialOwner"
      >
        {{ i18n(translations.addBeneficialOwner) }}
      </RevButton>
    </div>

    <div class="mt-48 flex justify-center">
      <RevButton class="w-full" variant="primary" @click="handleSaveAll">
        {{ i18n(translations.saveButton) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import {
  type CollapsedStates,
  RevAccordionItem,
} from '@ds/components/AccordionItem'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import { resetForm, setFormValues } from '@ds/components/Form/Form.actions'

import type { KybData, StakeHolderInfo } from '~/scopes/kyb/pages/Index.types'

// import type { StakeholderFormValues } from '../components/StakeholderForm/StakeholderForm.types'
import StakeholderForm from '../components/StakeholderForm/StakeholderForm.vue'

import translations from './BeneficialOwner.translations'

const FORM_ID = 'kyb_beneficial_owner_form'

// Types for secondary beneficial owners
type SecondaryBeneficialOwner = {
  id: string
  accordionState: CollapsedStates
  data: StakeHolderInfo | object
}

const props = defineProps<{
  kybData: KybData
}>()

const beneficialOwner = computed(() => props.kybData?.beneficialOwner)

const isSameAsAccountRequester = ref(
  props.kybData?.legalRepresentative?.sameAsAccountRequester ?? false,
)

const primaryAccordionState = ref<CollapsedStates>('expanded')

function handlePrimaryAccordionClick() {
  primaryAccordionState.value =
    primaryAccordionState.value === 'expanded' ? 'collapsed' : 'expanded'
}

const isSameAsLegalRepresentative = ref(
  props.kybData?.beneficialOwner?.primary?.sameAsLegalRepresentative ?? false,
)

// Secondary beneficial owners state
const secondaryBeneficialOwners = ref<SecondaryBeneficialOwner[]>([])

// Generate unique ID for new beneficial owners
function generateBeneficialOwnerId(): string {
  return `bo_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

// TODO: Implement form submission logic
// async function handleSubmit(formValues: StakeholderFormValues) {
//   try {
//     // eslint-disable-next-line no-console
//     console.log('Legal Representative form submitted:', formValues)
//     // eslint-disable-next-line no-console
//     console.log('Same as account requester:', isSameAsAccountRequester.value)
//   } catch (error) {
//     // console.error('Form submission error:', error)
//   }
// }

const isFormDisabled = ref(false)
const isAccountRequesterCheckboxDisabled = ref(false)
const isLegalRepresentativeCheckboxDisabled = ref(false)

function handleCheckSameAsAccountRequester(value: boolean) {
  if (value === true) {
    isFormDisabled.value = true
    isLegalRepresentativeCheckboxDisabled.value = true
    setFormValues(FORM_ID, {
      firstName: props.kybData.accountRequester.firstName,
      kanaFirstName: props.kybData.accountRequester.kanaFirstName,
      latinFirstName: props.kybData.accountRequester.latinFirstName,
      lastName: props.kybData.accountRequester.lastName,
      kanaLastName: props.kybData.accountRequester.kanaLastName,
      latinLastName: props.kybData.accountRequester.latinLastName,
      dateOfBirth: props.kybData.accountRequester.dateOfBirth,
      countryOfCitizenship: props.kybData.accountRequester.countryOfCitizenship,
      phoneNumber: props.kybData.accountRequester.phoneNumber,
      countryOfResidency: props.kybData.accountRequester.countryOfResidency,
      addressLine1: props.kybData.accountRequester.address?.addressLine1,
      addressLine2: props.kybData.accountRequester.address?.addressLine2,
      postalCode: props.kybData.accountRequester.address?.postalCode,
      city: props.kybData.accountRequester.address?.city,
      country: props.kybData.accountRequester.address?.country,
      identificationType:
        props.kybData.accountRequester.identityDocument?.identificationType,
      countryOfIssue:
        props.kybData.accountRequester.identityDocument?.countryOfIssue,
      photoIdFront: props.kybData.accountRequester.identityDocument?.front,
      photoIdBack: props.kybData.accountRequester.identityDocument?.back,
    })
  } else {
    isFormDisabled.value = false
    isLegalRepresentativeCheckboxDisabled.value = false

    resetForm(FORM_ID)
  }
}
function handleCheckSameAsLegalRepresentative(value: boolean) {
  if (value === true) {
    isFormDisabled.value = true
    isAccountRequesterCheckboxDisabled.value = true
    setFormValues(FORM_ID, {
      firstName: props.kybData.legalRepresentative.firstName,
      kanaFirstName: props.kybData.legalRepresentative.kanaFirstName,
      latinFirstName: props.kybData.legalRepresentative.latinFirstName,
      lastName: props.kybData.legalRepresentative.lastName,
      kanaLastName: props.kybData.legalRepresentative.kanaLastName,
      latinLastName: props.kybData.legalRepresentative.latinLastName,
      dateOfBirth: props.kybData.legalRepresentative.dateOfBirth,
      countryOfCitizenship:
        props.kybData.legalRepresentative.countryOfCitizenship,
      phoneNumber: props.kybData.legalRepresentative.phoneNumber,
      countryOfResidency: props.kybData.legalRepresentative.countryOfResidency,
      addressLine1: props.kybData.legalRepresentative.address?.addressLine1,
      addressLine2: props.kybData.legalRepresentative.address?.addressLine2,
      postalCode: props.kybData.legalRepresentative.address?.postalCode,
      city: props.kybData.legalRepresentative.address?.city,
      country: props.kybData.legalRepresentative.address?.country,
      identificationType:
        props.kybData.legalRepresentative.identityDocument?.identificationType,
      countryOfIssue:
        props.kybData.legalRepresentative.identityDocument?.countryOfIssue,
      photoIdFront: props.kybData.legalRepresentative.identityDocument?.front,
      photoIdBack: props.kybData.legalRepresentative.identityDocument?.back,
    })
  } else {
    isFormDisabled.value = false
    isAccountRequesterCheckboxDisabled.value = false

    resetForm(FORM_ID)
  }
}

// Secondary beneficial owners functions
function addSecondaryBeneficialOwner() {
  const newOwner: SecondaryBeneficialOwner = {
    id: generateBeneficialOwnerId(),
    accordionState: 'expanded',
    data: {},
  }
  secondaryBeneficialOwners.value.push(newOwner)
}

function removeSecondaryBeneficialOwner(index: number) {
  secondaryBeneficialOwners.value.splice(index, 1)
}

function handleSecondaryAccordionClick(index: number) {
  const owner = secondaryBeneficialOwners.value[index]
  if (owner) {
    owner.accordionState =
      owner.accordionState === 'expanded' ? 'collapsed' : 'expanded'
  }
}

async function handleSaveAll() {
  try {
    // TODO: Collect all form data and submit
    // eslint-disable-next-line no-console
    console.log('Saving all beneficial owners data...')
    // eslint-disable-next-line no-console
    console.log(
      'Primary beneficial owner same as account requester:',
      isSameAsAccountRequester.value,
    )
    // eslint-disable-next-line no-console
    console.log(
      'Primary beneficial owner same as legal representative:',
      isSameAsLegalRepresentative.value,
    )
    // eslint-disable-next-line no-console
    console.log(
      'Secondary beneficial owners count:',
      secondaryBeneficialOwners.value.length,
    )
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving beneficial owners:', error)
  }
}

const i18n = useI18n()
</script>
